import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  MessageCircle, Settings, Send, BarChart3, Shield,
  RefreshCw, Search, CheckCheck,
  XCircle, AlertTriangle, FileText, Phone, Plus, UserSearch,
  Loader2, Link2, Unplug, Zap, Calendar, Gauge, CalendarClock,
  Users, BookOpen, User, CheckCircle, X, Hash, Eye, ArrowLeft, Star, Bot, Pencil,
} from "lucide-react";
import {
  PageHeader, AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  StatsCard, ProgressBar, StatusBadge, EmptyState, AppBadge, Separator,
  Tabs, TabsList, TabsTrigger, TabsContent, ConfirmModal,
} from "../ui";
import { Form, Spinner } from "react-bootstrap";
import CommonToaster from "../Common/CommonToaster";
import CreateTemplateForm from "./CreateTemplateForm";
import SendMessageToStudent from "./SendMessageToStudent";
import WhatsAppChatInbox from "./WhatsAppChatInbox";
import ScheduledMessagesTab from "./ScheduledMessagesTab";
import AIAgentTab from "./AIAgentTab";
import KnowledgeBaseTab from "./KnowledgeBaseTab";
import { useSocket } from "../../contexts/SocketContext";
import apiClient from "../../utils/apiClient";
import "./whatsapp-dashboard.scss";

/* ── helpers ───────────────────────────────────────────────────────────────── */
const Loader = () => (
  <div className="wa-loader"><Loader2 size={20} className="wa-spin" /> Loading…</div>
);

const statusToVariant = (s) => {
  const map = { open: "active", assigned: "pending", resolved: "completed", closed: "inactive" };
  return map[s] || "outline";
};

const tplStatusToVariant = (s) => {
  const map = { APPROVED: "active", PENDING: "pending", REJECTED: "destructive", PAUSED: "inactive", DISABLED: "inactive" };
  return map[s] || "outline";
};

const TAB_LABELS = {
  inbox: { icon: MessageCircle, label: 'Inbox' },
  analytics: { icon: BarChart3, label: 'Analytics' },
  send: { icon: UserSearch, label: 'Send Message' },
  templates: { icon: FileText, label: 'Templates' },
  broadcast: { icon: Send, label: 'Broadcast' },
  scheduled: { icon: CalendarClock, label: 'Scheduled' },
  automation: { icon: Zap, label: 'Automation' },
  aiagent: { icon: Bot, label: 'AI Agent' },
  knowledge: { icon: BookOpen, label: 'Knowledge' },
  config: { icon: Settings, label: 'Config' },
  optin: { icon: Shield, label: 'Opt-In' },
};

/* ══════════════════════════════════════════════════════════════════════════════
   WhatsApp Dashboard
   ══════════════════════════════════════════════════════════════════════════════ */
const WhatsAppDashboard = () => {
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const toastRef = useRef(null);
  toastRef.current = (message, type = "success") => setToast({ show: true, message, type });

  const showToast = useCallback((msg, type) => toastRef.current(msg, type), []);
  const { unreadWhatsAppCount, clearUnreadWhatsApp } = useSocket();

  /* ── Persist active tab across reloads via URL hash ──────────────────────── */
  const validTabs = useMemo(() => ['inbox', 'analytics', 'send', 'templates', 'broadcast', 'scheduled', 'automation', 'aiagent', 'knowledge', 'config', 'optin'], []);
  const getInitialTab = useCallback(() => {
    const hash = window.location.hash.replace('#', '');
    return validTabs.includes(hash) ? hash : 'inbox';
  }, [validTabs]);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  const handleTabChange = useCallback((value) => {
    setActiveTab(value);
    setMobileDropdownOpen(false);
    window.history.replaceState(null, '', `#${value}`);
    if (value === 'inbox') clearUnreadWhatsApp();
  }, [clearUnreadWhatsApp]);

  // Clear unread count when landing directly on inbox
  useEffect(() => {
    if (activeTab === 'inbox') clearUnreadWhatsApp();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mobile: render tab selector in top nav portal ────────────────────────
  const portalTarget = document.getElementById('top-nav-portal');
  const ActiveIcon = TAB_LABELS[activeTab]?.icon || MessageCircle;

  useEffect(() => {
    // Close dropdown on outside click
    if (!mobileDropdownOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.wa-mobile-tab-selector')) setMobileDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileDropdownOpen]);

  // Cleanup portal content on unmount
  useEffect(() => {
    return () => setMobileDropdownOpen(false);
  }, []);

  return (
    <div className="wa-dashboard">
      {/* Portal: mobile tab selector in top nav bar */}
      {portalTarget && createPortal(
        <div className="wa-mobile-tab-selector">
          <button
            type="button"
            className={`wa-mobile-tab-btn ${mobileDropdownOpen ? 'open' : ''}`}
            onClick={() => setMobileDropdownOpen(prev => !prev)}
          >
            <ActiveIcon size={14} />
            <span>{TAB_LABELS[activeTab]?.label || 'Inbox'}</span>
            {unreadWhatsAppCount > 0 && activeTab !== 'inbox' && (
              <span className="wa-tab-badge">{unreadWhatsAppCount > 99 ? '99+' : unreadWhatsAppCount}</span>
            )}
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={`wa-mobile-chevron ${mobileDropdownOpen ? 'open' : ''}`}>
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {mobileDropdownOpen && (
            <div className="wa-mobile-tab-menu">
              {validTabs.map(tab => {
                const TabIcon = TAB_LABELS[tab]?.icon || MessageCircle;
                return (
                  <button
                    key={tab}
                    type="button"
                    className={`wa-mobile-tab-item ${tab === activeTab ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab)}
                  >
                    <TabIcon size={14} />
                    <span>{TAB_LABELS[tab]?.label}</span>
                    {tab === 'inbox' && unreadWhatsAppCount > 0 && (
                      <span className="wa-tab-badge">{unreadWhatsAppCount > 99 ? '99+' : unreadWhatsAppCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>,
        portalTarget
      )}

      <PageHeader
        title="WhatsApp"
        description="Manage WhatsApp notifications, conversations, and templates"
        icon={MessageCircle}
      />

      <Tabs value={activeTab} onChange={handleTabChange} className="wa-tabs">
        <TabsList>
          <TabsTrigger value="inbox">
            <MessageCircle size={14} /> Inbox
            {unreadWhatsAppCount > 0 && activeTab !== 'inbox' && (
              <span className="wa-tab-badge">{unreadWhatsAppCount > 99 ? '99+' : unreadWhatsAppCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics"><BarChart3 size={14} /> Analytics</TabsTrigger>
          <TabsTrigger value="send"><UserSearch size={14} /> Send Message</TabsTrigger>
          <TabsTrigger value="templates"><FileText size={14} /> Templates</TabsTrigger>
          <TabsTrigger value="broadcast"><Send size={14} /> Broadcast</TabsTrigger>
          <TabsTrigger value="scheduled"><CalendarClock size={14} /> Scheduled</TabsTrigger>
          <TabsTrigger value="automation"><Zap size={14} /> Automation</TabsTrigger>
          <TabsTrigger value="aiagent"><Bot size={14} /> AI Agent</TabsTrigger>
          <TabsTrigger value="knowledge"><BookOpen size={14} /> Knowledge</TabsTrigger>
          <TabsTrigger value="config"><Settings size={14} /> Config</TabsTrigger>
          <TabsTrigger value="optin"><Shield size={14} /> Opt-In</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox"><InboxTab showToast={showToast} /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab showToast={showToast} /></TabsContent>
        <TabsContent value="send"><SendMessageToStudent showToast={showToast} /></TabsContent>
        <TabsContent value="templates"><TemplatesTab showToast={showToast} /></TabsContent>
        <TabsContent value="broadcast"><BroadcastTab showToast={showToast} /></TabsContent>
        <TabsContent value="scheduled"><ScheduledMessagesTab showToast={showToast} /></TabsContent>
        <TabsContent value="automation"><AutomationTab showToast={showToast} /></TabsContent>
        <TabsContent value="aiagent"><AIAgentTab showToast={showToast} /></TabsContent>
        <TabsContent value="knowledge"><KnowledgeBaseTab showToast={showToast} /></TabsContent>
        <TabsContent value="config"><ConfigTab showToast={showToast} /></TabsContent>
        <TabsContent value="optin"><OptInTab showToast={showToast} /></TabsContent>
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/* ─── Analytics Tab ────────────────────────────────────────────────────────── */
const AnalyticsTab = ({ showToast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await apiClient.get(`/whatsapp/analytics?days=${days}`);
      setData(d);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load analytics", "error");
    }
    setLoading(false);
  }, [days, showToast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;

  if (!data) {
    return (
      <EmptyState
        icon={<BarChart3 size={32} />}
        title="No Analytics Data"
        description="Configure WhatsApp in the Config tab first to start collecting analytics."
      />
    );
  }

  const ov = data.overview;
  const convStats = data.conversationStats || {};
  const tplAnalytics = data.templateAnalytics || {};

  return (
    <div className="wa-analytics">
      {/* Controls */}
      <div className="wa-analytics__controls">
        <div className="settings-field" style={{ maxWidth: 160 }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <AppButton variant="outline" size="sm" onClick={load}>
          <RefreshCw size={13} /> Refresh
        </AppButton>
      </div>

      {/* KPI Cards */}
      <div className="wa-stats-grid">
        <StatsCard title="Messages Sent" value={ov?.totals?.outbound || 0} icon={Send} iconColor="blue" />
        <StatsCard title="Delivered" value={ov?.outboundStatuses?.delivered || 0} icon={CheckCheck} iconColor="emerald" />
        <StatsCard title="Read" value={ov?.outboundStatuses?.read || 0} icon={CheckCheck} iconColor="violet" />
        <StatsCard title="Failed" value={ov?.outboundStatuses?.failed || 0} icon={XCircle} iconColor="rose" />
        <StatsCard title="Inbound" value={ov?.totals?.inbound || 0} icon={MessageCircle} iconColor="amber" />
      </div>

      {/* Rates */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><BarChart3 size={18} /> Delivery Rates</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-rates-grid">
            {[
              { label: "Delivery Rate", pct: ov?.rates?.deliveryRate || 0, color: "emerald" },
              { label: "Read Rate", pct: ov?.rates?.readRate || 0, color: "blue" },
              { label: "Fail Rate", pct: ov?.rates?.failRate || 0, color: "rose" },
            ].map(r => (
              <div key={r.label} className="wa-rate-item">
                <div className="wa-rate-item__header">
                  <span>{r.label}</span>
                  <strong>{r.pct}%</strong>
                </div>
                <ProgressBar value={r.pct} color={r.color} size="sm" />
              </div>
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Conversation Stats */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><MessageCircle size={18} /> Conversations</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-conv-stats">
            {["open", "assigned", "resolved", "closed"].map(s => (
              <div key={s} className="wa-conv-stat-item">
                <span className="wa-conv-stat-item__value">{convStats[s] || 0}</span>
                <StatusBadge status={statusToVariant(s)}>{s}</StatusBadge>
              </div>
            ))}
          </div>
        </AppCardContent>
      </AppCard>

      {/* Template Usage */}
      {Object.keys(tplAnalytics).length > 0 && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><FileText size={18} /> Template Performance</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="wa-table-wrap">
              <table className="wa-table">
                <thead>
                  <tr>
                    <th>Template</th><th>Total</th><th>Delivered</th><th>Read</th><th>Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(tplAnalytics).map(([name, s]) => (
                    <tr key={name}>
                      <td className="wa-table__mono">{name}</td>
                      <td>{s.total}</td>
                      <td className="wa-table__emerald">{s.delivered || 0}</td>
                      <td className="wa-table__blue">{s.read || 0}</td>
                      <td className="wa-table__rose">{s.failed || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
};

/* ─── Inbox Tab (uses shared WhatsApp Chat Inbox) ──────────────────────────── */
const InboxTab = ({ showToast }) => {
  const { socket } = useSocket();
  const fetchConversations = useCallback(async (statusFilter) => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    const { data } = await apiClient.get(`/whatsapp/conversations${q}`);
    return (data.conversations || []).map(c => ({
      _id: c._id,
      name: c.customerName || c.studentId?.name || "",
      phone: c.customerPhone,
      preview: c.lastMessagePreview,
      updatedAt: c.updatedAt,
      unreadCount: c.unreadCount,
      status: c.status,
      studentName: c.studentId?.name,
      aiPaused: !!c.aiPaused,
      aiHandoffReason: c.aiHandoffReason,
      aiTurnCount: c.aiTurnCount,
    }));
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    const { data } = await apiClient.get(`/whatsapp/conversations/${convId}/messages`);
    return (data.messages || []).reverse(); // API returns newest-first
  }, []);

  const sendReply = useCallback(async (conversation, payload) => {
    // Backwards-compat: a plain string still works.
    const body = typeof payload === "string" ? { messageType: "text", text: payload } : payload;
    await apiClient.post(`/whatsapp/conversations/${conversation._id}/reply`, body);
  }, []);

  const updateStatus = useCallback(async (convId, status) => {
    await apiClient.patch(`/whatsapp/conversations/${convId}/status`, { status });
  }, []);

  return (
    <WhatsAppChatInbox
      fetchConversations={fetchConversations}
      fetchMessages={fetchMessages}
      sendReply={sendReply}
      updateStatus={updateStatus}
      showToast={showToast}
      socket={socket}
    />
  );
};

/* ─── Templates Tab ────────────────────────────────────────────────────────── */
/* ── Template Detail Viewer ────────────────────────────────────────────────── */
const TemplateDetailView = ({ template, onBack }) => {
  if (!template) return null;
  const header = template.components?.find(c => c.type === 'HEADER');
  const body = template.components?.find(c => c.type === 'BODY');
  const footer = template.components?.find(c => c.type === 'FOOTER');
  const buttons = template.components?.find(c => c.type === 'BUTTONS');

  return (
    <AppCard>
      <AppCardHeader>
        <div className="wa-card-header-row">
          <AppCardTitle>
            <AppButton variant="ghost" size="sm" onClick={onBack} title="Back to list">
              <ArrowLeft size={16} />
            </AppButton>
            <FileText size={18} /> {template.name}
          </AppCardTitle>
          <div className="wa-inline-actions">
            <AppBadge variant="secondary" size="sm">{template.language}</AppBadge>
            <AppBadge variant="secondary" size="sm">{template.category || '—'}</AppBadge>
            <StatusBadge status={tplStatusToVariant(template.status)}>{template.status}</StatusBadge>
          </div>
        </div>
      </AppCardHeader>
      <AppCardContent>
        <div className="wa-tpl-detail">
          {/* WhatsApp-style bubble preview */}
          <div className="wa-tpl-detail__preview">
            <div className="wa-tpl-detail__preview-label">Message Preview</div>
            <div className="wa-bc-preview__bubble">
              {header?.text && <div className="wa-bc-preview__header">{header.text}</div>}
              {header?.format === 'IMAGE' && <div className="wa-tpl-detail__media-tag">📷 Image Header</div>}
              {header?.format === 'VIDEO' && <div className="wa-tpl-detail__media-tag">🎥 Video Header</div>}
              {header?.format === 'DOCUMENT' && <div className="wa-tpl-detail__media-tag">📄 Document Header</div>}
              {body?.text && <div className="wa-bc-preview__body">{body.text}</div>}
              {footer?.text && <div className="wa-bc-preview__footer">{footer.text}</div>}
              {buttons?.buttons?.length > 0 && (
                <div className="wa-bc-preview__buttons">
                  {buttons.buttons.map((btn, i) => (
                    <div key={i} className="wa-bc-preview__btn">
                      {btn.type === 'URL' ? '🔗' : btn.type === 'PHONE_NUMBER' ? '📞' : '↩️'} {btn.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Raw components breakdown */}
          <div className="wa-tpl-detail__components">
            <div className="wa-tpl-detail__preview-label">Components</div>
            {header && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">HEADER ({header.format || 'TEXT'})</div>
                <div className="wa-tpl-detail__comp-text">{header.text || `[${header.format} media]`}</div>
              </div>
            )}
            {body && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">BODY</div>
                <div className="wa-tpl-detail__comp-text">{body.text}</div>
              </div>
            )}
            {footer && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">FOOTER</div>
                <div className="wa-tpl-detail__comp-text">{footer.text}</div>
              </div>
            )}
            {buttons?.buttons?.length > 0 && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">BUTTONS</div>
                {buttons.buttons.map((btn, i) => (
                  <div key={i} className="wa-tpl-detail__btn-row">
                    <AppBadge variant="secondary" size="sm">{btn.type}</AppBadge>
                    <span>{btn.text}</span>
                    {btn.url && <span className="wa-tpl-detail__btn-url">{btn.url}</span>}
                    {btn.phone_number && <span className="wa-tpl-detail__btn-url">{btn.phone_number}</span>}
                  </div>
                ))}
              </div>
            )}
            {template.parameterCount > 0 && (
              <div className="wa-tpl-detail__comp">
                <div className="wa-tpl-detail__comp-type">PARAMETERS</div>
                <div className="wa-tpl-detail__comp-text">{template.parameterCount} variable(s) expected</div>
              </div>
            )}
          </div>
        </div>
      </AppCardContent>
    </AppCard>
  );
};

const TemplatesTab = ({ showToast }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteTemplateName, setDeleteTemplateName] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [showRecommended, setShowRecommended] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [usingTemplate, setUsingTemplate] = useState(null);
  const [customizing, setCustomizing] = useState(null); // recommended tpl being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/whatsapp/templates");
      setTemplates(data.templates || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncTemplates = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/whatsapp/templates/sync");
      showToast(data.message || "Templates synced");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Sync failed", "error");
    }
    setSyncing(false);
  };

  const handleCreate = async (formData) => {
    const { data } = await apiClient.post("/whatsapp/templates/create", formData);
    showToast(data.message || "Template submitted");
    setShowCreate(false);
    load();
  };

  const handleDelete = (name) => setDeleteTemplateName(name);

  const confirmDelete = async () => {
    const name = deleteTemplateName;
    setDeleteTemplateName(null);
    setDeleting(name);
    try {
      await apiClient.delete(`/whatsapp/templates/${encodeURIComponent(name)}`);
      showToast("Template deleted");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Delete failed", "error");
    }
    setDeleting(null);
  };

  const loadRecommended = async () => {
    setShowRecommended(true);
    setLoadingRecommended(true);
    try {
      const { data } = await apiClient.get("/whatsapp/recommended-templates");
      setRecommended(data.templates || []);
    } catch { /* ignore */ }
    setLoadingRecommended(false);
  };

  const adoptRecommended = async (tpl) => {
    setUsingTemplate(tpl._id);
    try {
      const { data } = await apiClient.post("/whatsapp/templates/use-recommended", { templateId: tpl._id });
      showToast(data.message || "Template submitted for approval");
      setShowRecommended(false);
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to use template", "error");
    }
    setUsingTemplate(null);
  };

  const confirmModal = (
    <ConfirmModal
      show={!!deleteTemplateName}
      onHide={() => setDeleteTemplateName(null)}
      onConfirm={confirmDelete}
      title="Delete Template"
      message={`Delete template "${deleteTemplateName}"? This will also delete it from Meta.`}
      confirmText="Delete"
      variant="danger"
    />
  );

  if (viewTemplate) {
    return <><TemplateDetailView template={viewTemplate} onBack={() => setViewTemplate(null)} />{confirmModal}</>;
  }

  // Customize-and-submit a recommended template (pre-filled editor flow).
  if (customizing) {
    const handleCustomizeSubmit = async (formData) => {
      // Goes through the standard create endpoint — submits the (possibly edited)
      // template to Meta on the institute's own WABA.
      const { data } = await apiClient.post("/whatsapp/templates/create", formData);
      showToast(data.message || "Template submitted");
      setCustomizing(null);
      setShowRecommended(false);
      load();
    };
    return (
      <>
        <AppCard>
          <AppCardHeader>
            <div className="wa-card-header-row">
              <AppCardTitle><Pencil size={18} /> Customize: {customizing.name}</AppCardTitle>
              <AppButton size="sm" variant="ghost" onClick={() => setCustomizing(null)}>
                <ArrowLeft size={13} /> Back
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardContent>
            <CreateTemplateForm
              initialValues={customizing}
              onSubmit={handleCustomizeSubmit}
              onCancel={() => setCustomizing(null)}
              submitLabel="Submit customized template"
            />
          </AppCardContent>
        </AppCard>
        {confirmModal}
      </>
    );
  }

  if (showRecommended) {
    return (
      <>
        <AppCard>
          <AppCardHeader>
            <div className="wa-card-header-row">
              <AppCardTitle><Star size={18} /> Recommended Templates</AppCardTitle>
              <AppButton size="sm" variant="ghost" onClick={() => setShowRecommended(false)}>
                <ArrowLeft size={13} /> Back to Templates
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardContent>
            {loadingRecommended ? <Loader /> : recommended.length === 0 ? (
              <EmptyState
                icon={<Star size={32} />}
                title="No Recommended Templates"
                description="No recommended templates are available yet. Your platform admin will add them soon."
              />
            ) : (
              <div className="wa-rec-grid">
                {recommended.map(t => {
                  const header = t.components?.find(c => c.type === "HEADER");
                  const body = t.components?.find(c => c.type === "BODY");
                  const footer = t.components?.find(c => c.type === "FOOTER");
                  const btns = t.components?.find(c => c.type === "BUTTONS");
                  const alreadyExists = templates.some(
                    et => et.name === t.name && et.language === t.language
                  );
                  return (
                    <div className="wa-rec-card" key={t._id}>
                      <div className="wa-rec-card__bubble">
                        {header?.format === "TEXT" && header.text && (
                          <div className="wa-rec-card__header">{header.text}</div>
                        )}
                        {header?.format === "IMAGE" && (
                          <div className="wa-rec-card__media">📷 Image Header</div>
                        )}
                        {header?.format === "VIDEO" && (
                          <div className="wa-rec-card__media">🎥 Video Header</div>
                        )}
                        {header?.format === "DOCUMENT" && (
                          <div className="wa-rec-card__media">📄 Document Header</div>
                        )}
                        {body?.text && (
                          <div className="wa-rec-card__body">{body.text}</div>
                        )}
                        {footer?.text && (
                          <div className="wa-rec-card__footer">{footer.text}</div>
                        )}
                        {btns?.buttons?.length > 0 && (
                          <div className="wa-rec-card__buttons">
                            {btns.buttons.map((btn, i) => (
                              <div key={i} className="wa-rec-card__btn">
                                {btn.type === "URL" ? "🔗" : btn.type === "PHONE_NUMBER" ? "📞" : "↩️"} {btn.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="wa-rec-card__info">
                        <span className="wa-rec-card__name">{t.name}</span>
                        <div className="wa-rec-card__meta">
                          <AppBadge variant="secondary" size="sm">{t.category || "—"}</AppBadge>
                          <AppBadge variant="secondary" size="sm">{t.language}</AppBadge>
                          {t.parameterCount > 0 && (
                            <AppBadge variant="outline" size="sm">{t.parameterCount} param{t.parameterCount > 1 ? "s" : ""}</AppBadge>
                          )}
                        </div>
                      </div>
                      <div className="wa-rec-card__action">
                        {alreadyExists ? (
                          <AppBadge variant="outline" size="sm">Already added</AppBadge>
                        ) : (
                          <>
                            <AppButton
                              size="sm"
                              variant="outline"
                              onClick={() => setCustomizing(t)}
                              className="wa-rec-card__use-btn"
                            >
                              <Pencil size={13} /> Customize
                            </AppButton>
                            <AppButton
                              size="sm"
                              onClick={() => adoptRecommended(t)}
                              disabled={usingTemplate === t._id}
                              className="wa-rec-card__use-btn"
                            >
                              {usingTemplate === t._id ? (
                                <><Loader2 size={13} className="wa-spin" /> Submitting...</>
                              ) : (
                                <><Plus size={13} /> Use as-is</>
                              )}
                            </AppButton>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </AppCardContent>
        </AppCard>
        {confirmModal}
      </>
    );
  }

  if (showCreate) {
    return (
      <>
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><FileText size={18} /> Create Template</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <CreateTemplateForm onSubmit={handleCreate} onCancel={() => setShowCreate(false)} />
          </AppCardContent>
        </AppCard>
        {confirmModal}
      </>
    );
  }

  return (
    <>
    <AppCard>
      <AppCardHeader>
        <div className="wa-card-header-row">
          <AppCardTitle><FileText size={18} /> Templates</AppCardTitle>
          <div className="wa-inline-actions">
            <span className="wa-text-muted wa-text-sm">{templates.length} template(s)</span>
            <AppButton size="sm" variant="outline" onClick={loadRecommended}>
              <Star size={13} /> Recommended
            </AppButton>
            <AppButton size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={13} /> Create Template
            </AppButton>
            <AppButton size="sm" onClick={syncTemplates} disabled={syncing}>
              <RefreshCw size={13} className={syncing ? "wa-spin" : ""} />
              {syncing ? "Syncing..." : "Sync from Meta"}
            </AppButton>
          </div>
        </div>
      </AppCardHeader>
      <AppCardContent>
        {loading ? <Loader /> : templates.length === 0 ? (
          <EmptyState
            icon={<FileText size={32} />}
            title="No Templates"
            description='Click "Sync from Meta" to fetch your approved templates, or create a new one.'
          />
        ) : (
          <div className="wa-table-wrap">
            <table className="wa-table">
              <thead>
                <tr><th>Name</th><th>Language</th><th>Category</th><th>Status</th><th>Params</th><th>Used</th><th></th></tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t._id}>
                    <td className="wa-table__mono">{t.name}</td>
                    <td>{t.language}</td>
                    <td><AppBadge variant="secondary" size="sm">{t.category || "—"}</AppBadge></td>
                    <td><StatusBadge status={tplStatusToVariant(t.status)}>{t.status}</StatusBadge></td>
                    <td>{t.parameterCount}</td>
                    <td>{t.usageCount || 0}</td>
                    <td className="wa-table__actions">
                      <AppButton
                        variant="ghost" size="sm"
                        onClick={() => setViewTemplate(t)}
                        title="View template"
                      >
                        <Eye size={13} />
                      </AppButton>
                      <AppButton
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(t.name)}
                        disabled={deleting === t.name}
                        title="Delete template"
                      >
                        {deleting === t.name ? <Loader2 size={13} className="wa-spin" /> : <XCircle size={13} />}
                      </AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AppCardContent>
    </AppCard>
    {confirmModal}
    </>
  );
};

/* ─── Broadcast Tab ────────────────────────────────────────────────────────── */

/* ── Template variable extraction helpers (shared with SendMessageToStudent) ── */
function _extractParams(comp) {
  if (!comp?.text) return [];
  const params = [];
  const regex = /\{\{([^}]+)\}\}/g;
  let match;
  const seen = new Set();
  while ((match = regex.exec(comp.text)) !== null) {
    const raw = match[1].trim();
    if (seen.has(raw)) continue;
    seen.add(raw);
    let example = "";
    if (comp.example?.body_text_named_params) {
      const np = comp.example.body_text_named_params.find(p => p.param_name === raw);
      if (np) example = np.example || "";
    } else if (comp.example?.body_text?.[0]) {
      const idx = parseInt(raw, 10);
      if (!isNaN(idx) && idx >= 1) example = comp.example.body_text[0][idx - 1] || "";
    } else if (comp.example?.header_text) {
      const idx = parseInt(raw, 10);
      if (!isNaN(idx) && idx >= 1) example = comp.example.header_text[idx - 1] || "";
    }
    const isPositional = /^\d+$/.test(raw);
    params.push({ key: raw, label: isPositional ? `Parameter ${raw}` : raw.replace(/_/g, " "), example });
  }
  return params;
}

function _extractBtnParams(comp) {
  if (comp?.type !== "BUTTONS" || !comp.buttons) return [];
  const params = [];
  comp.buttons.forEach((btn, i) => {
    if (btn.type === "URL" && btn.url) {
      const regex = /\{\{(\d+)\}\}/g;
      let match;
      // Extract the static base URL before the {{1}} placeholder
      const baseUrl = btn.url.replace(/\{\{\d+\}\}/g, "").replace(/\/$/, "");
      while ((match = regex.exec(btn.url)) !== null) {
        const raw = match[1].trim();
        let example = btn.example?.[0] || "";
        params.push({ key: `btn_${i}_${raw}`, label: `Button "${btn.text}" URL suffix`, example, buttonIndex: i, baseUrl });
      }
    }
  });
  return params;
}

function _extractTemplateVars(template) {
  if (!template?.components) return { header: [], body: [], button: [] };
  const hc = template.components.find(c => c.type === "HEADER");
  const bc = template.components.find(c => c.type === "BODY");
  const btc = template.components.find(c => c.type === "BUTTONS");
  return { header: _extractParams(hc), body: _extractParams(bc), button: _extractBtnParams(btc) };
}

function _buildComponents(vars, values) {
  const components = [];
  const mkParam = (p, prefix) => {
    const param = { type: "text", text: values[`${prefix}_${p.key}`] || "" };
    if (!/^\d+$/.test(p.key)) param.parameter_name = p.key;
    return param;
  };
  if (vars.header.length) components.push({ type: "header", parameters: vars.header.map(p => mkParam(p, "header")) });
  if (vars.body.length) components.push({ type: "body", parameters: vars.body.map(p => mkParam(p, "body")) });
  if (vars.button.length) {
    const byBtn = {};
    vars.button.forEach(p => { if (!byBtn[p.buttonIndex]) byBtn[p.buttonIndex] = []; byBtn[p.buttonIndex].push(p); });
    Object.entries(byBtn).forEach(([idx, params]) => {
      components.push({ type: "button", sub_type: "url", index: Number(idx), parameters: params.map(p => mkParam(p, "button")) });
    });
  }
  return components;
}

function _fillPreview(text, params, values, prefix) {
  if (!text) return text;
  let result = text;
  params.forEach(p => { result = result.replace(`{{${p.key}}}`, values[`${prefix}_${p.key}`] || `{{${p.key}}}`); });
  return result;
}

const AUDIENCE_TYPES = [
  { value: "all", label: "All Students", icon: Users, desc: "Every active student in your institute" },
  { value: "course", label: "By Course", icon: BookOpen, desc: "All students enrolled in a specific course" },
  { value: "batch", label: "By Batch", icon: Hash, desc: "All students in a specific batch" },
  { value: "students", label: "Specific Students", icon: UserSearch, desc: "Search and select individual students" },
  { value: "external_numbers", label: "External Numbers", icon: Phone, desc: "Enter phone numbers manually (not in LMS)" },
];

const BroadcastTab = ({ showToast }) => {
  // ── Data loading ──
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Audience ──
  const [audienceType, setAudienceType] = useState("all");
  const [studentStatus, setStudentStatus] = useState("active");
  const [courseId, setCourseId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [studentSearching, setStudentSearching] = useState(false);
  const searchTimer = useRef(null);

  // ── External numbers ──
  const [externalNumbersText, setExternalNumbersText] = useState("");
  const parsedExternalNumbers = useMemo(() => {
    if (!externalNumbersText.trim()) return [];
    return externalNumbersText
      .split(/[\n,;]+/)
      .map(n => n.trim().replace(/[\s()+\-]/g, ''))
      .filter(n => n.length >= 10);
  }, [externalNumbersText]);

  // ── Template ──
  const [templateName, setTemplateName] = useState("");
  const [paramValues, setParamValues] = useState({});
  // paramModes tracks each param as 'dynamic' or 'static'
  // key format: "header_1", "body_name", "button_btn_0_1"
  const [paramModes, setParamModes] = useState({});

  // ── Recipient count ──
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const countTimer = useRef(null);

  // ── Send ──
  const [confirmBroadcast, setConfirmBroadcast] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // ── Derived ──
  const selectedTemplate = useMemo(() => templates.find(t => t.name === templateName) || null, [templates, templateName]);
  const templateVars = useMemo(() => selectedTemplate ? _extractTemplateVars(selectedTemplate) : { header: [], body: [], button: [] }, [selectedTemplate]);
  const totalParams = templateVars.header.length + templateVars.body.length + templateVars.button.length;

  const filteredBatches = useMemo(() => {
    if (audienceType === "batch" && courseId) {
      return batches.filter(b => (b.course?._id || b.course) === courseId);
    }
    return batches;
  }, [batches, audienceType, courseId]);

  const allParamsFilled = totalParams === 0 || [
    ...templateVars.header.map(p => `header_${p.key}`),
    ...templateVars.body.map(p => `body_${p.key}`),
    ...templateVars.button.map(p => `button_${p.key}`),
  ].every(k => {
    // Dynamic params are always "filled" (resolved per-student on backend)
    if (paramModes[k] === 'dynamic' && paramValues[k]) return true;
    // Static params need a non-empty value
    return paramModes[k] !== 'dynamic' && paramValues[k]?.trim();
  });

  const audienceReady = (
    (audienceType === "all") ||
    (audienceType === "course" && courseId) ||
    (audienceType === "batch" && batchId) ||
    (audienceType === "students" && selectedStudents.length > 0) ||
    (audienceType === "external_numbers" && parsedExternalNumbers.length > 0)
  );

  const canSend = templateName && allParamsFilled && audienceReady;

  // ── Load initial data ──
  useEffect(() => {
    (async () => {
      try {
        const [tplRes, optRes] = await Promise.all([
          apiClient.get("/whatsapp/templates"),
          apiClient.get("/whatsapp/broadcast/options"),
        ]);
        setTemplates((tplRes.data.templates || []).filter(t => t.status === "APPROVED"));
        setCourses(optRes.data.courses || []);
        setBatches(optRes.data.batches || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  // ── Reset params when template changes ──
  useEffect(() => { setParamValues({}); setParamModes({}); }, [templateName]);

  // ── Reset state when audience or template changes ──
  useEffect(() => { setConfirmBroadcast(false); setShowConfirmModal(false); setSendResult(null); }, [audienceType, courseId, batchId, selectedStudents.length, parsedExternalNumbers.length, templateName, studentStatus]);

  // ── Fetch recipient count when audience changes ──
  useEffect(() => {
    if (countTimer.current) clearTimeout(countTimer.current);
    setRecipientCount(null);

    if (!audienceReady) return;

    // External numbers: count locally, no API call needed
    if (audienceType === "external_numbers") {
      setRecipientCount(parsedExternalNumbers.length);
      return;
    }

    countTimer.current = setTimeout(async () => {
      setCountLoading(true);
      try {
        const body = { audienceType, studentStatus };
        if (audienceType === "course") body.courseId = courseId;
        if (audienceType === "batch") body.batchId = batchId;
        if (audienceType === "students") body.studentIds = selectedStudents.map(s => s._id);
        const { data } = await apiClient.post("/whatsapp/broadcast/count", body);
        setRecipientCount(data.count);
      } catch {
        setRecipientCount(null);
      }
      setCountLoading(false);
    }, 300);

    return () => { if (countTimer.current) clearTimeout(countTimer.current); };
  }, [audienceType, courseId, batchId, selectedStudents, audienceReady, studentStatus, parsedExternalNumbers]);

  // ── Student search ──
  const handleStudentSearch = useCallback((value) => {
    setStudentQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) { setStudentResults([]); setStudentSearching(false); return; }
    setStudentSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/whatsapp/students/search?q=${encodeURIComponent(value.trim())}`);
        setStudentResults(data.students || []);
      } catch { setStudentResults([]); }
      setStudentSearching(false);
    }, 350);
  }, []);

  const addStudent = (s) => {
    if (!selectedStudents.find(x => x._id === s._id)) {
      setSelectedStudents(prev => [...prev, s]);
    }
    setStudentQuery("");
    setStudentResults([]);
  };

  const removeStudent = (id) => {
    setSelectedStudents(prev => prev.filter(s => s._id !== id));
  };

  // ── Send broadcast ──
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setSendResult(null);
    try {
      const payload = {
        templateName,
        languageCode: selectedTemplate?.language || "en",
        audienceType,
        studentStatus,
        confirmBroadcast: true,
      };

      // Build paramConfig for per-student personalization
      if (totalParams > 0) {
        const buildEntries = (params, prefix) => params.map(p => {
          const k = `${prefix}_${p.key}`;
          const mode = paramModes[k] || "static";
          return {
            key: p.key,
            type: mode,
            variable: mode === "dynamic" ? (paramValues[k] || "") : "",
            value: mode === "static" ? (paramValues[k] || "") : "",
            ...(p.buttonIndex !== undefined ? { buttonIndex: p.buttonIndex } : {}),
          };
        });

        payload.paramConfig = {
          header: buildEntries(templateVars.header, "header"),
          body: buildEntries(templateVars.body, "body"),
          button: buildEntries(templateVars.button, "button"),
        };

        // Also send legacy components for backward compat (static-only fallback)
        payload.components = _buildComponents(templateVars, paramValues);
      }

      if (audienceType === "course") payload.courseId = courseId;
      if (audienceType === "batch") payload.batchId = batchId;
      if (audienceType === "students") payload.studentIds = selectedStudents.map(s => s._id);
      if (audienceType === "external_numbers") payload.externalNumbers = parsedExternalNumbers;

      const { data } = await apiClient.post("/whatsapp/broadcast", payload);
      showToast(data.message || "Broadcast queued");
      setSendResult({ success: true, queued: data.queued });
      setConfirmBroadcast(false);
      setShowConfirmModal(false);
    } catch (err) {
      showToast(err?.response?.data?.message || "Broadcast failed", "error");
      setSendResult({ success: false, message: err?.response?.data?.message || "Failed" });
    }
    setSending(false);
  };

  if (loading) return <Loader />;

  return (
    <div className="wa-broadcast">
      {/* ── Step 1: Audience Selection ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Users size={18} /> Step 1 — Select Audience</AppCardTitle>
          <p className="settings-card-desc">Choose who will receive this broadcast message</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-bc-audience-grid">
            {AUDIENCE_TYPES.map(a => {
              const Icon = a.icon;
              return (
                <div
                  key={a.value}
                  className={`wa-bc-audience-card ${audienceType === a.value ? "wa-bc-audience-card--active" : ""}`}
                  onClick={() => { setAudienceType(a.value); setCourseId(""); setBatchId(""); setSelectedStudents([]); setExternalNumbersText(""); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && setAudienceType(a.value)}
                >
                  <div className="wa-bc-audience-card__icon"><Icon size={20} /></div>
                  <div className="wa-bc-audience-card__label">{a.label}</div>
                  <div className="wa-bc-audience-card__desc">{a.desc}</div>
                </div>
              );
            })}
          </div>

          {/* Student Status Filter (not applicable for external numbers) */}
          {audienceType !== "external_numbers" && (
          <div className="wa-bc-filter-section" style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, marginBottom: 4 }}>
            <Form.Label style={{ marginBottom: 0, fontSize: "0.82rem", fontWeight: 500 }}>Send to:</Form.Label>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                className={`wa-mode-btn ${studentStatus === "active" ? "wa-mode-btn--active" : ""}`}
                onClick={() => setStudentStatus("active")}
              >
                <CheckCircle size={13} /> Active Students
              </button>
              <button
                type="button"
                className={`wa-mode-btn ${studentStatus === "all" ? "wa-mode-btn--active" : ""}`}
                onClick={() => setStudentStatus("all")}
              >
                <Users size={13} /> All Students
              </button>
            </div>
          </div>
          )}

          {/* Course Selector */}
          {audienceType === "course" && (
            <div className="wa-bc-filter-section">
              <Form.Group>
                <Form.Label>Select Course</Form.Label>
                <Form.Select value={courseId} onChange={e => setCourseId(e.target.value)}>
                  <option value="">Choose a course...</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.name}{c.title ? ` (${c.title})` : ""}</option>)}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {/* Batch Selector */}
          {audienceType === "batch" && (
            <div className="wa-bc-filter-section">
              <Form.Group className="mb-2">
                <Form.Label>Filter by Course (optional)</Form.Label>
                <Form.Select value={courseId} onChange={e => { setCourseId(e.target.value); setBatchId(""); }}>
                  <option value="">All Courses</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Select Batch</Form.Label>
                <Form.Select value={batchId} onChange={e => setBatchId(e.target.value)}>
                  <option value="">Choose a batch...</option>
                  {filteredBatches.map(b => (
                    <option key={b._id} value={b._id}>
                      {b.name}{b.course?.name ? ` — ${b.course.name}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {/* Student Picker */}
          {audienceType === "students" && (
            <div className="wa-bc-filter-section">
              <Form.Label>Search &amp; Select Students</Form.Label>
              <div className="wa-bc-student-search-box">
                <Search size={15} className="wa-bc-student-search-icon" />
                <input
                  type="text"
                  className="wa-bc-student-search-input"
                  placeholder="Search by name or mobile number..."
                  value={studentQuery}
                  onChange={e => handleStudentSearch(e.target.value)}
                />
                {studentSearching && <Spinner animation="border" size="sm" />}
              </div>

              {studentResults.length > 0 && (
                <div className="wa-bc-student-results">
                  {studentResults.filter(s => !selectedStudents.find(x => x._id === s._id)).map(s => (
                    <div key={s._id} className="wa-bc-student-result" onClick={() => addStudent(s)} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && addStudent(s)}>
                      <User size={14} />
                      <span className="wa-bc-student-result__name">{s.name}</span>
                      <span className="wa-bc-student-result__meta">{s.mobile}</span>
                      {s.course?.name && <span className="wa-bc-student-result__meta">{s.course.name}</span>}
                    </div>
                  ))}
                </div>
              )}

              {selectedStudents.length > 0 && (
                <div className="wa-bc-selected-students">
                  <div className="wa-bc-selected-students__label">
                    Selected ({selectedStudents.length})
                    <button className="wa-bc-clear-all" onClick={() => setSelectedStudents([])}>Clear all</button>
                  </div>
                  <div className="wa-bc-chips">
                    {selectedStudents.map(s => (
                      <div key={s._id} className="wa-bc-chip">
                        <User size={12} />
                        <span>{s.name}</span>
                        <button className="wa-bc-chip__remove" onClick={() => removeStudent(s._id)} title="Remove"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* External Numbers Input */}
          {audienceType === "external_numbers" && (
            <div className="wa-bc-filter-section">
              <label className="wa-form-label" style={{ marginBottom: 6 }}>Phone Numbers</label>
              <textarea
                className="wa-form-input wa-form-textarea"
                rows={5}
                value={externalNumbersText}
                onChange={e => setExternalNumbersText(e.target.value)}
                placeholder={"Enter phone numbers (one per line, or comma/semicolon separated)\n\nExamples:\n919876543210\n918765432109\n917654321098"}
              />
              <div className="wa-form-hint" style={{ marginTop: 6 }}>
                Include country code (e.g. 91 for India). Numbers not in your LMS will also receive the message.
              </div>
              {parsedExternalNumbers.length > 0 && (
                <div className="wa-form-hint" style={{ marginTop: 4 }}>
                  <strong>{parsedExternalNumbers.length}</strong> valid number{parsedExternalNumbers.length !== 1 ? "s" : ""} detected
                </div>
              )}
            </div>
          )}

          {/* Recipient Count Badge */}
          {audienceReady && (
            <div className="wa-bc-recipient-count">
              <Users size={14} />
              {countLoading ? (
                <span>Counting recipients…</span>
              ) : recipientCount !== null ? (
                <span><strong>{recipientCount}</strong> {audienceType === "external_numbers" ? "phone number" : (studentStatus === "all" ? "" : "active ") + "student"}{recipientCount !== 1 ? "s" : ""} will receive this broadcast</span>
              ) : (
                <span>Unable to estimate recipients</span>
              )}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Step 2: Template Selection & Configuration ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><FileText size={18} /> Step 2 — Select Template &amp; Fill Variables</AppCardTitle>
          <p className="settings-card-desc">Choose an approved WhatsApp template and fill in any dynamic variables</p>
        </AppCardHeader>
        <AppCardContent>
          {templates.length === 0 ? (
            <EmptyState
              icon={<FileText size={32} />}
              title="No Approved Templates"
              description='Sync your templates from Meta in the Templates tab, or create a new one.'
            />
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Template</Form.Label>
                <Form.Select value={templateName} onChange={e => setTemplateName(e.target.value)}>
                  <option value="">Select a template...</option>
                  {templates.map(t => (
                    <option key={t._id} value={t.name}>
                      {t.name} ({t.language}) — {t.category || "MARKETING"}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Variable inputs */}
              {selectedTemplate && totalParams > 0 && (
                <div className="wa-bc-vars">
                  <div className="wa-bc-vars__title">Template Variables ({totalParams})</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary, #9ca3af)", marginBottom: 10 }}>
                    For each variable, choose <strong>Dynamic</strong> (different per student, e.g., student name) or <strong>Static</strong> (same value for everyone).
                  </div>
                  {templateVars.header.length > 0 && (
                    <div className="wa-bc-vars__group">
                      <div className="wa-bc-vars__group-label">Header</div>
                      {templateVars.header.map(p => {
                        const k = `header_${p.key}`;
                        const mode = paramModes[k] || "static";
                        return (
                          <div key={k} className="mb-2">
                            <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button type="button" className={`wa-mode-btn ${mode === "dynamic" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "dynamic" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Dynamic</button>
                                <button type="button" className={`wa-mode-btn ${mode === "static" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "static" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Static</button>
                              </div>
                              {mode === "dynamic" ? (
                                <Form.Select size="sm" style={{ maxWidth: 220 }} value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}>
                                  <option value="">-- Select variable --</option>
                                  <option value="student_name">Student Name</option>
                                  <option value="course_name">Course Name</option>
                                  <option value="batch_name">Batch Name</option>
                                  <option value="institute_name">Institute Name</option>
                                  <option value="pending_fees">Pending Fees</option>
                                  <option value="current_date">Current Date</option>
                                </Form.Select>
                              ) : (
                                <Form.Control size="sm" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))} placeholder={p.example || `Enter ${p.label}...`} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {templateVars.body.length > 0 && (
                    <div className="wa-bc-vars__group">
                      <div className="wa-bc-vars__group-label">Body</div>
                      {templateVars.body.map(p => {
                        const k = `body_${p.key}`;
                        const mode = paramModes[k] || "static";
                        return (
                          <div key={k} className="mb-2">
                            <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button type="button" className={`wa-mode-btn ${mode === "dynamic" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "dynamic" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Dynamic</button>
                                <button type="button" className={`wa-mode-btn ${mode === "static" ? "wa-mode-btn--active" : ""}`} style={{ fontSize: "0.7rem", padding: "2px 8px" }} onClick={() => { setParamModes(m => ({ ...m, [k]: "static" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Static</button>
                              </div>
                              {mode === "dynamic" ? (
                                <Form.Select size="sm" style={{ maxWidth: 220 }} value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}>
                                  <option value="">-- Select variable --</option>
                                  <option value="student_name">Student Name</option>
                                  <option value="course_name">Course Name</option>
                                  <option value="batch_name">Batch Name</option>
                                  <option value="institute_name">Institute Name</option>
                                  <option value="pending_fees">Pending Fees</option>
                                  <option value="current_date">Current Date</option>
                                </Form.Select>
                              ) : (
                                <Form.Control size="sm" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))} placeholder={p.example || `Enter ${p.label}...`} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {templateVars.button.length > 0 && (
                    <div className="wa-bc-vars__group">
                      <div className="wa-bc-vars__group-label">Buttons</div>
                      {templateVars.button.map(p => {
                        const k = `button_${p.key}`;
                        return (
                          <Form.Group key={k} className="mb-2">
                            <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                            {p.baseUrl && (
                              <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary, #9ca3af)", marginBottom: 4, fontFamily: "monospace", wordBreak: "break-all" }}>
                                Base URL: <strong>{p.baseUrl}</strong>/
                                <span style={{ color: "var(--blue-400, #60a5fa)" }}>{"{{suffix}}"}</span>
                              </div>
                            )}
                            <Form.Control
                              size="sm"
                              value={paramValues[k] || ""}
                              onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}
                              placeholder={p.example || `Enter only the path/suffix (not full URL)...`}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: "0.68rem" }}>
                              Only enter the suffix part. The base URL is fixed in your Meta template. Final link: {p.baseUrl}/{paramValues[k] || "..."}
                            </Form.Text>
                          </Form.Group>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Live Preview */}
              {selectedTemplate && (
                <BroadcastPreview template={selectedTemplate} vars={templateVars} values={paramValues} modes={paramModes} />
              )}
            </>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Step 3: Review & Send ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Send size={18} /> Step 3 — Review &amp; Send</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          {/* Summary */}
          <div className="wa-bc-summary">
            <div className="wa-bc-summary__row">
              <span className="wa-bc-summary__label">Audience</span>
              <span>{AUDIENCE_TYPES.find(a => a.value === audienceType)?.label || audienceType}
                {audienceType === "course" && courseId && ` — ${courses.find(c => c._id === courseId)?.name || ""}`}
                {audienceType === "batch" && batchId && ` — ${batches.find(b => b._id === batchId)?.name || ""}`}
                {audienceType === "students" && ` — ${selectedStudents.length} selected`}
                {audienceType === "external_numbers" && ` — ${parsedExternalNumbers.length} number(s)`}
              </span>
            </div>
            <div className="wa-bc-summary__row">
              <span className="wa-bc-summary__label">Recipients</span>
              <span>{countLoading ? "Counting…" : recipientCount !== null ? `${recipientCount} recipient(s)` : "—"}</span>
            </div>
            <div className="wa-bc-summary__row">
              <span className="wa-bc-summary__label">Template</span>
              <span>{templateName || "Not selected"}</span>
            </div>
          </div>

          {sendResult && (
            <div className={`wa-alert ${sendResult.success ? "wa-alert--success" : "wa-alert--warning"}`}>
              {sendResult.success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
              {sendResult.success ? `Broadcast queued for ${sendResult.queued} recipient(s)!` : sendResult.message}
            </div>
          )}

          <Separator />
          <div className="settings-save-row">
            <AppButton onClick={() => setShowConfirmModal(true)} disabled={!canSend || sending}>
              <Send size={14} /> Send Broadcast
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Broadcast Confirmation Modal ── */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => { if (!sending) setShowConfirmModal(false); }}
        onConfirm={handleSend}
        title="Confirm Broadcast"
        variant="warning"
        confirmText="Send Broadcast"
        cancelText="Cancel"
        loading={sending}
        message={
          <div className="wa-confirm-broadcast">
            {/* Preview */}
            {selectedTemplate && (
              <BroadcastPreview template={selectedTemplate} vars={templateVars} values={paramValues} modes={paramModes} />
            )}
            {/* Summary */}
            <div className="wa-bc-summary" style={{ marginTop: 12 }}>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Audience</span>
                <span>{AUDIENCE_TYPES.find(a => a.value === audienceType)?.label || audienceType}
                  {audienceType === "course" && courseId && ` — ${courses.find(c => c._id === courseId)?.name || ""}`}
                  {audienceType === "batch" && batchId && ` — ${batches.find(b => b._id === batchId)?.name || ""}`}
                  {audienceType === "students" && ` — ${selectedStudents.length} selected`}
                  {audienceType === "external_numbers" && ` — ${parsedExternalNumbers.length} number(s)`}
                </span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Recipients</span>
                <span><strong>{recipientCount ?? "?"}</strong> recipient(s)</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Template</span>
                <span>{templateName}</span>
              </div>
            </div>
            <div className="wa-alert wa-alert--warning" style={{ marginTop: 10, marginBottom: 0 }}>
              <AlertTriangle size={14} />
              This will send a WhatsApp message to <strong>{recipientCount ?? "?"} recipient(s)</strong>. This action cannot be undone.
            </div>
          </div>
        }
      />
    </div>
  );
};

/* ── Broadcast Template Preview ── */
const DYNAMIC_VAR_LABELS = { student_name: "Student Name", course_name: "Course Name", batch_name: "Batch Name", institute_name: "Institute Name", pending_fees: "Pending Fees", current_date: "Current Date" };

const BroadcastPreview = ({ template, vars, values, modes = {} }) => {
  const hc = template.components?.find(c => c.type === "HEADER");
  const bc = template.components?.find(c => c.type === "BODY");
  const fc = template.components?.find(c => c.type === "FOOTER");
  const btc = template.components?.find(c => c.type === "BUTTONS");

  // Build preview values that show dynamic labels for dynamic params
  const previewValues = { ...values };
  Object.entries(modes).forEach(([k, mode]) => {
    if (mode === "dynamic" && values[k]) {
      previewValues[k] = `[${DYNAMIC_VAR_LABELS[values[k]] || values[k]}]`;
    }
  });

  const headerText = hc?.format === "TEXT" ? _fillPreview(hc.text, vars.header, previewValues, "header") : null;
  const bodyText = bc?.text ? _fillPreview(bc.text, vars.body, previewValues, "body") : null;
  const footerText = fc?.text || null;

  return (
    <div className="wa-bc-preview">
      <div className="wa-bc-preview__label">Preview</div>
      <div className="wa-bc-preview__bubble">
        {headerText && <div className="wa-bc-preview__header">{headerText}</div>}
        {bodyText && <div className="wa-bc-preview__body">{bodyText}</div>}
        {footerText && <div className="wa-bc-preview__footer">{footerText}</div>}
        {btc?.buttons?.length > 0 && (
          <div className="wa-bc-preview__buttons">
            {btc.buttons.map((btn, i) => <div key={btn.text || i} className="wa-bc-preview__btn">{btn.text}</div>)}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Automation Tab ───────────────────────────────────────────────────────── */
const AutomationTab = ({ showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [classStarted, setClassStarted] = useState({
    enabled: false,
    useTemplate: true,
    templateName: "",
    languageCode: "en",
    paramMapping: [],
    messageText: "Hi {student_name}, your class for {course_name} ({batch_name}) has started! Join now from your dashboard.",
  });

  // Test message state
  const [batches, setBatches] = useState([]);
  const [testBatchId, setTestBatchId] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // Load settings + templates + batches on mount
  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, tplRes, batchRes] = await Promise.allSettled([
          apiClient.get("/institute/settings"),
          apiClient.get("/whatsapp/templates"),
          apiClient.get("/batches/list"),
        ]);
        if (settingsRes.status === "fulfilled") {
          const wa = settingsRes.value.data.settings?.whatsappAutomation?.classStarted || {};
          setClassStarted({
            enabled: wa.enabled || false,
            useTemplate: wa.useTemplate !== false,
            templateName: wa.templateName || "",
            languageCode: wa.languageCode || "en",
            paramMapping: wa.paramMapping || [],
            messageText: wa.messageText || "Hi {student_name}, your class for {course_name} ({batch_name}) has started! Join now from your dashboard.",
          });
        }
        if (tplRes.status === "fulfilled") {
          setTemplates((tplRes.value.data.templates || []).filter(t => t.status === "APPROVED"));
        }
        if (batchRes.status === "fulfilled") {
          const bd = batchRes.value.data;
          setBatches(bd?.batches || bd || []);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/institute/settings", {
        whatsappAutomation: { classStarted },
      });
      showToast("Automation settings saved!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save", "error");
    }
    setSaving(false);
  };

  const handleSendTest = async () => {
    if (!testBatchId) {
      showToast("Select a batch first", "error");
      return;
    }
    setSendingTest(true);
    try {
      const payload = { batchId: testBatchId };
      if (testPhone.trim()) payload.phone = testPhone.trim();
      const res = await apiClient.post("/whatsapp/test-class-notification", payload);
      showToast(res.data.message || "Test sent!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Test send failed", "error");
    }
    setSendingTest(false);
  };

  // Live preview for custom text mode
  const previewText = (classStarted.messageText || "")
    .replace(/\{student_name\}/g, "John Doe")
    .replace(/\{course_name\}/g, "Web Development")
    .replace(/\{batch_name\}/g, "WEB_DEV_2")
    .replace(/\{instructor_name\}/g, "Prof. Smith")
    .replace(/\{institute_name\}/g, "My Institute");

  if (loading) return <Loader />;

  const selectedTpl = templates.find(t => t.name === classStarted.templateName);
  const bodyComp = selectedTpl?.components?.find(c => c.type === "BODY");

  return (
    <div className="wa-automation">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Zap size={18} /> WhatsApp Automation</AppCardTitle>
          <p className="settings-card-desc">
            Configure automatic WhatsApp messages triggered by platform events.
            Choose between an approved Meta template or a custom message with placeholders.
          </p>
        </AppCardHeader>
        <AppCardContent>

          {/* ── Class Started Automation ── */}
          <div className="wa-automation-card">
            <div className="wa-automation-card__header">
              <div className="wa-automation-card__info">
                <div className="wa-automation-card__icon" style={{ background: "var(--blue-50, #eff6ff)", color: "var(--blue-600, #2563eb)" }}>
                  <MessageCircle size={20} />
                </div>
                <div>
                  <div className="wa-automation-card__title">Class Started</div>
                  <div className="wa-automation-card__desc">
                    Automatically send a WhatsApp message to all batch students when an instructor starts a live class.
                  </div>
                </div>
              </div>
              <Form.Check
                type="switch"
                id="wa-auto-class-started"
                checked={classStarted.enabled}
                onChange={e => setClassStarted(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </div>

            {classStarted.enabled && (
              <div className="wa-automation-card__body">

                {/* ── Message Mode Toggle ── */}
                <div className="wa-mode-toggle" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <button
                    type="button"
                    className={`wa-mode-btn ${classStarted.useTemplate ? "wa-mode-btn--active" : ""}`}
                    onClick={() => setClassStarted(prev => ({ ...prev, useTemplate: true }))}
                  >
                    <FileText size={14} /> Meta Template
                  </button>
                  <button
                    type="button"
                    className={`wa-mode-btn ${!classStarted.useTemplate ? "wa-mode-btn--active" : ""}`}
                    onClick={() => setClassStarted(prev => ({ ...prev, useTemplate: false }))}
                  >
                    <MessageCircle size={14} /> Custom Message
                  </button>
                </div>

                {/* ── Template Mode ── */}
                {classStarted.useTemplate && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label className="wa-bc-var-label">Template</Form.Label>
                      <Form.Select
                        size="sm"
                        value={classStarted.templateName}
                        onChange={e => {
                          const tplName = e.target.value;
                          const tpl = templates.find(t => t.name === tplName);
                          const paramCount = tpl?.parameterCount || 0;
                          // Initialize param mapping array when template changes
                          const existingMapping = classStarted.paramMapping || [];
                          const newMapping = Array.from({ length: paramCount }, (_, i) => existingMapping[i] || "");
                          setClassStarted(prev => ({ ...prev, templateName: tplName, paramMapping: newMapping }));
                        }}
                      >
                        <option value="">Select an approved template...</option>
                        {templates.map(t => (
                          <option key={t._id} value={t.name}>
                            {t.name} ({t.language}) — {t.parameterCount} param(s)
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted" style={{ fontSize: "0.72rem" }}>
                        Choose a template and map each parameter to the data you want sent.
                      </Form.Text>
                    </Form.Group>

                    {selectedTpl && (
                      <>
                        <div className="wa-automation-card__preview">
                          <div className="wa-bc-preview__label">Template Preview</div>
                          <div className="wa-bc-preview__bubble">
                            {bodyComp?.text && (
                              <div className="wa-bc-preview__body">{bodyComp.text}</div>
                            )}
                          </div>
                        </div>

                        {/* ── Parameter Mapping ── */}
                        {(selectedTpl.parameterCount || 0) > 0 && (
                          <div style={{ marginTop: 12, marginBottom: 16 }}>
                            <div className="wa-bc-var-label" style={{ marginBottom: 8 }}>
                              <Settings size={13} /> Map Template Parameters
                            </div>
                            <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary, #9ca3af)", marginBottom: 8 }}>
                              Assign what data each template variable should contain. This must match your Meta-approved template.
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {Array.from({ length: selectedTpl.parameterCount }, (_, idx) => (
                                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <span style={{
                                    fontSize: "0.8rem", fontWeight: 600, minWidth: 50,
                                    color: "var(--text-secondary, #d1d5db)",
                                    fontFamily: "monospace",
                                  }}>
                                    {`{{${idx + 1}}}`}
                                  </span>
                                  <span style={{ fontSize: "0.75rem", color: "var(--text-tertiary, #9ca3af)" }}>→</span>
                                  <Form.Select
                                    size="sm"
                                    style={{ maxWidth: 220 }}
                                    value={(classStarted.paramMapping || [])[idx] || ""}
                                    onChange={e => {
                                      const newMapping = [...(classStarted.paramMapping || [])];
                                      // Ensure array is long enough
                                      while (newMapping.length <= idx) newMapping.push("");
                                      newMapping[idx] = e.target.value;
                                      setClassStarted(prev => ({ ...prev, paramMapping: newMapping }));
                                    }}
                                  >
                                    <option value="">-- Select variable --</option>
                                    <option value="student_name">Student Name</option>
                                    <option value="course_name">Course Name</option>
                                    <option value="batch_name">Batch Name</option>
                                    <option value="instructor_name">Instructor Name</option>
                                    <option value="institute_name">Institute Name</option>
                                    <option value="pending_fees">Pending Fees</option>
                                    <option value="current_date">Current Date</option>
                                  </Form.Select>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <Form.Group className="mb-2">
                      <Form.Label className="wa-bc-var-label">Language Code</Form.Label>
                      <Form.Control
                        size="sm"
                        style={{ maxWidth: 180 }}
                        value={classStarted.languageCode}
                        onChange={e => setClassStarted(prev => ({ ...prev, languageCode: e.target.value }))}
                        placeholder="en"
                      />
                    </Form.Group>
                  </>
                )}

                {/* ── Custom Message Mode ── */}
                {!classStarted.useTemplate && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label className="wa-bc-var-label">Message Text</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={classStarted.messageText}
                        onChange={e => setClassStarted(prev => ({ ...prev, messageText: e.target.value }))}
                        placeholder="Type your message with placeholders..."
                        style={{ fontSize: "0.85rem" }}
                      />
                      <Form.Text className="text-muted" style={{ fontSize: "0.72rem" }}>
                        Available placeholders: <code>{"{student_name}"}</code> <code>{"{course_name}"}</code> <code>{"{batch_name}"}</code> <code>{"{instructor_name}"}</code> <code>{"{institute_name}"}</code>
                      </Form.Text>
                    </Form.Group>

                    <div className="wa-automation-card__preview">
                      <div className="wa-bc-preview__label">Live Preview</div>
                      <div className="wa-bc-preview__bubble">
                        <div className="wa-bc-preview__body" style={{ whiteSpace: "pre-wrap" }}>{previewText}</div>
                      </div>
                    </div>

                    <div className="wa-alert wa-alert--warning" style={{ marginTop: 12, fontSize: "0.75rem" }}>
                      <AlertTriangle size={13} />
                      Custom text messages only work within Meta's 24-hour customer service window.
                      Use a Meta Template if students haven't messaged your number recently.
                    </div>
                  </>
                )}

                {/* ── Test Message ── */}
                <Separator />
                <div style={{ marginTop: 12 }}>
                  <div className="wa-bc-var-label" style={{ marginBottom: 8 }}>
                    <Send size={13} /> Send Test Message
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <Form.Group style={{ flex: "1 1 200px", minWidth: 180 }}>
                      <Form.Label style={{ fontSize: "0.72rem", marginBottom: 2 }}>Batch</Form.Label>
                      <Form.Select
                        size="sm"
                        value={testBatchId}
                        onChange={e => setTestBatchId(e.target.value)}
                      >
                        <option value="">Select a batch...</option>
                        {batches.map(b => (
                          <option key={b._id} value={b._id}>
                            {b.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group style={{ flex: "0 1 180px", minWidth: 140 }}>
                      <Form.Label style={{ fontSize: "0.72rem", marginBottom: 2 }}>Phone (optional)</Form.Label>
                      <Form.Control
                        size="sm"
                        value={testPhone}
                        onChange={e => setTestPhone(e.target.value)}
                        placeholder="e.g. 9876543210"
                      />
                    </Form.Group>
                    <AppButton size="sm" onClick={handleSendTest} disabled={sendingTest || !testBatchId}>
                      {sendingTest ? <Spinner animation="border" size="sm" /> : <><Send size={13} /> Send Test</>}
                    </AppButton>
                  </div>
                  <Form.Text className="text-muted" style={{ fontSize: "0.68rem" }}>
                    Sends one message using current settings. Leave phone blank to send to the first student in the batch.
                  </Form.Text>
                </div>

              </div>
            )}
          </div>

          <Separator />
          <div className="settings-save-row">
            <AppButton onClick={handleSave} disabled={saving}>
              {saving ? (
                <><Spinner animation="border" size="sm" /> Saving…</>
              ) : (
                <><Settings size={14} /> Save Automation Settings</>
              )}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    </div>
  );
};

/* ─── Config Tab ───────────────────────────────────────────────────────────── */
const ConfigTab = ({ showToast }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/whatsapp/connect/status");
      setStatus(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  /* Open Meta Embedded Signup — fetches App ID from backend */
  const handleConnect = async () => {
    try {
      const { data } = await apiClient.get("/whatsapp/connect/config");
      if (!data.configured || !data.appId) {
        showToast("WhatsApp Embedded Signup is not configured by the administrator yet.", "error");
        return;
      }
      const scope = 'business_management, whatsapp_business_management,whatsapp_business_messaging';
      // Use canonical redirect URI from backend so custom-domain institutes
      // always redirect through the whitelisted lmscampus.com domain
      const redirectUri = data.redirectUri || `${window.location.origin}/whatsapp/connect/callback`;
      const url = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${data.appId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&response_type=code`;
      window.open(url, 'meta_wa_signup', 'width=600,height=700,scrollbars=yes');
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load connect configuration.", "error");
    }
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const handleDisconnect = () => setShowDisconnectConfirm(true);

  const confirmDisconnect = async () => {
    setShowDisconnectConfirm(false);
    setDisconnecting(true);
    try {
      await apiClient.post("/whatsapp/disconnect");
      showToast("WhatsApp disconnected");
      setStatus({ connected: false });
    } catch (err) {
      showToast(err?.response?.data?.message || "Disconnect failed", "error");
    }
    setDisconnecting(false);
  };

  const handleSyncTemplates = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/whatsapp/templates/sync");
      showToast(data.message || "Templates synced");
    } catch (err) {
      showToast(err?.response?.data?.message || "Sync failed", "error");
    }
    setSyncing(false);
  };

  const handleRepairWebhook = async () => {
    setRepairing(true);
    try {
      const { data } = await apiClient.post("/whatsapp/connect/repair-webhook");
      showToast(data.message || "Webhook repaired successfully");
    } catch (err) {
      showToast(err?.response?.data?.message || "Repair failed", "error");
    }
    setRepairing(false);
  };

  if (loading) return <Loader />;

  /* ── Not connected ── */
  if (!status?.connected) {
    return (
      <AppCard>
        <AppCardContent>
          <div className="wa-connect-empty">
            <div className="wa-connect-empty__icon">
              <MessageCircle size={40} />
            </div>
            <h3>WhatsApp Not Connected</h3>
            <p className="wa-text-muted">
              Connect your WhatsApp Business account to start sending notifications,
              manage conversations, and engage with your students.
            </p>
            <AppButton onClick={handleConnect}>
              <Link2 size={16} /> Connect WhatsApp
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    );
  }

  /* ── Connected ── */
  const qualityColor = { GREEN: 'emerald', YELLOW: 'amber', RED: 'rose' }[status.qualityRating] || 'blue';

  return (
    <div className="wa-config-connected">
      {/* Status banner */}
      <div className="wa-alert wa-alert--success">
        <CheckCheck size={14} />
        WhatsApp is connected{status.connectedVia === 'embedded_signup' ? ' via Meta Embedded Signup' : ''}.
      </div>

      {/* Info cards */}
      <div className="wa-stats-grid">
        <StatsCard
          title="Phone Number"
          value={status.displayPhoneNumber || status.phoneNumberId}
          icon={Phone}
          iconColor="blue"
        />
        <StatsCard
          title="Quality Rating"
          value={status.qualityRating || 'N/A'}
          icon={Gauge}
          iconColor={qualityColor}
        />
        <StatsCard
          title="Messaging Tier"
          value={status.messagingLimit ? status.messagingLimit.replace('TIER_', '').replace('K', 'K/day') : 'N/A'}
          icon={Zap}
          iconColor="violet"
        />
        <StatsCard
          title="Connected Since"
          value={status.connectedAt ? new Date(status.connectedAt).toLocaleDateString() : 'N/A'}
          icon={Calendar}
          iconColor="amber"
        />
      </div>

      {/* Details card */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Settings size={18} /> Connection Details</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-detail-grid">
            <div className="wa-detail-row">
              <span className="wa-detail-label">WABA ID</span>
              <span>{status.wabaId}</span>
            </div>
            <div className="wa-detail-row">
              <span className="wa-detail-label">Phone Number ID</span>
              <span>{status.phoneNumberId}</span>
            </div>
            <div className="wa-detail-row">
              <span className="wa-detail-label">Daily Message Limit</span>
              <span>{status.dailyMessageLimit || 1000}</span>
            </div>
            <div className="wa-detail-row">
              <span className="wa-detail-label">Connected Via</span>
              <span>
                <StatusBadge status="active">
                  {status.connectedVia === 'embedded_signup' ? 'Embedded Signup' : 'Manual'}
                </StatusBadge>
              </span>
            </div>
          </div>

          <Separator />

          <div className="wa-config-actions">
            <AppButton size="sm" onClick={handleSyncTemplates} disabled={syncing}>
              <RefreshCw size={14} className={syncing ? 'wa-spin' : ''} />
              {syncing ? 'Syncing...' : 'Sync Templates'}
            </AppButton>
            <AppButton variant="outline" size="sm" onClick={handleRepairWebhook} disabled={repairing}>
              <RefreshCw size={14} className={repairing ? 'wa-spin' : ''} />
              {repairing ? 'Repairing...' : 'Repair Webhook'}
            </AppButton>
            <AppButton variant="outline" size="sm" onClick={handleConnect}>
              <Link2 size={14} /> Reconnect
            </AppButton>
            <AppButton variant="destructive" size="sm" onClick={handleDisconnect} disabled={disconnecting}>
              <Unplug size={14} /> {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      <ConfirmModal
        show={showDisconnectConfirm}
        onHide={() => setShowDisconnectConfirm(false)}
        onConfirm={confirmDisconnect}
        title="Disconnect WhatsApp"
        message="Are you sure you want to disconnect WhatsApp? Notifications will stop sending."
        confirmText="Disconnect"
        variant="danger"
      />
    </div>
  );
};

/* ─── Opt-In Management Tab ────────────────────────────────────────────────── */
const OptInTab = ({ showToast }) => {
  const [search, setSearch] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkIds, setBulkIds] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);

  const checkStatus = async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/whatsapp/optin/status/${search.trim()}`);
      setResult(data);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to check", "error");
      setResult(null);
    }
    setLoading(false);
  };

  const recordOptIn = async (studentId) => {
    try {
      await apiClient.post("/whatsapp/optin", { studentId, phone: result?.record?.phone || "", method: "manual_admin" });
      showToast("Opt-in recorded");
      checkStatus();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed", "error");
    }
  };

  const recordOptOut = async (studentId) => {
    try {
      await apiClient.post("/whatsapp/optout", { studentId, reason: "admin_action" });
      showToast("Opt-out recorded");
      checkStatus();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed", "error");
    }
  };

  const handleBulkOptIn = async () => {
    const ids = bulkIds.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (!ids.length) { showToast("Enter at least one Student ID", "warning"); return; }
    if (ids.length > 500) { showToast("Maximum 500 students per bulk operation", "warning"); return; }
    setBulkLoading(true);
    try {
      const { data } = await apiClient.post("/whatsapp/optin/bulk", { studentIds: ids });
      showToast(data.message || `Opt-in recorded for ${data.total} student(s)`);
      setBulkIds("");
    } catch (err) {
      showToast(err?.response?.data?.message || "Bulk opt-in failed", "error");
    }
    setBulkLoading(false);
  };

  return (
    <>
      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Shield size={18} /> Check Individual Student</AppCardTitle>
          <p className="settings-card-desc">
            WhatsApp messages are only sent to students who have opted in. Look up consent status here.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="wa-optin-search">
            <div className="settings-field" style={{ flex: 1, maxWidth: 320 }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Enter Student ID to check..."
                onKeyDown={e => { if (e.key === "Enter") checkStatus(); }}
              />
            </div>
            <AppButton size="sm" onClick={checkStatus} disabled={loading}>
              <Search size={13} /> {loading ? "Checking..." : "Check"}
            </AppButton>
          </div>

          {result && (
            <div className="wa-optin-result">
              <div className="wa-optin-result__info">
                <span>Opt-In Status:</span>
                <StatusBadge status={result.optedIn ? "active" : "inactive"}>
                  {result.optedIn ? "Opted In" : "Not Opted In"}
                </StatusBadge>
                {result.record?.optInAt && (
                  <span className="wa-text-muted wa-text-sm">
                    since {new Date(result.record.optInAt).toLocaleDateString()}
                  </span>
                )}
                {result.record?.optInMethod && (
                  <span className="wa-text-muted wa-text-sm">via {result.record.optInMethod}</span>
                )}
              </div>
              <div>
                {result.optedIn ? (
                  <AppButton variant="outline" size="sm" onClick={() => recordOptOut(search.trim())}>Record Opt-Out</AppButton>
                ) : (
                  <AppButton size="sm" onClick={() => recordOptIn(search.trim())}>Record Opt-In</AppButton>
                )}
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Shield size={18} /> Bulk Opt-In</AppCardTitle>
          <p className="settings-card-desc">Opt-in multiple students at once (up to 500)</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-field">
            <label>Student IDs</label>
            <textarea
              rows={3}
              value={bulkIds}
              onChange={e => setBulkIds(e.target.value)}
              placeholder="Paste Student IDs (one per line or comma-separated)..."
              style={{ maxWidth: 500 }}
            />
            <span className="wa-text-muted wa-text-xs">
              Only students with a phone number will be opted in.
            </span>
          </div>

          <Separator />
          <div className="settings-save-row">
            <AppButton size="sm" onClick={handleBulkOptIn} disabled={bulkLoading}>
              {bulkLoading ? "Processing..." : "Bulk Opt-In"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    </>
  );
};

export default WhatsAppDashboard;
